<template>
  <div class="animation-wrapper">
    <div class="anim-controls">
      <HoverPopover :message="$t('viewer.controls.skeleton')">
        <div class="control-btn" @click="toggleSkeletonVisibility">
          <BaseIcon iconName="skull"></BaseIcon>
        </div>
      </HoverPopover>

      <HoverPopover :message="$t('viewer.controls.panRotate')">
        <div class="control-btn" @click="togglePanMode">
          <BaseIcon :iconName="isPanModeEnabled ? 'pan' : 'rotate'"></BaseIcon>
        </div>
      </HoverPopover>

      <HoverPopover :message="$t('viewer.controls.resetCamera')">
        <div class="control-btn" @click="resetCamera">
          <BaseIcon iconName="camera"></BaseIcon>
        </div>
      </HoverPopover>


    </div>

    <div ref="threejsContainer" class="threejs-container"></div>

    <div class="scale-controls">
      <HoverPopover :message="$t('viewer.controls.zoomIn')">
        <div class="control-btn" @click="zoomIn">
          <BaseIcon iconName="plus"></BaseIcon>
        </div>
      </HoverPopover>
      <HoverPopover :message="$t('viewer.controls.zoomOut')">
        <div class="control-btn" @click="zoomOut">
          <BaseIcon iconName="minus"></BaseIcon>
        </div>
      </HoverPopover>
    </div>
  </div>

  <div class="animation-footer">
    <div class="spacer"></div>

    <div class="playbar">
      <div
        class="progress-container"
        @click="setAnimationProgress($event)"
        @mousedown="startDragging"
        @mouseup="stopDragging"
        @mouseleave="stopDragging"
        @mousemove="dragUpdate"
      >
        <div
          class="progress-bar"
          :style="{ width: progressBarWidth + '%' }"
        ></div>
      </div>

      <div class="time-codes">
        <div>{{ formattedCurrentTime }}</div>
        <div>{{ formattedDuration }}</div>
      </div>
      <div class="playbar-controls">
        <div class="control-btn rotate" @click="previousFrame">
          <BaseIcon iconName="fastForward"></BaseIcon>
        </div>
        <div class="control-btn" @click="toggleAnimation">
          <BaseIcon :iconName="iconName"></BaseIcon>
        </div>
        <div class="control-btn" @click="nextFrame">
          <BaseIcon iconName="fastForward"></BaseIcon>
        </div>
        <div class="control-btn" @click="resetToFirstFrame">
          <BaseIcon iconName="rotateLeft"></BaseIcon>
        </div>
      </div>
    </div>

    <div class="control-btn" @click="openSupportPopup">
      <BaseIcon iconName="help"></BaseIcon>
    </div>

    <SupportPopup :show="showSupportPopup" @close="showSupportPopup = false" />
  </div>

</template>

<script setup>
import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import HoverPopover from "@/components/HoverPopover.vue";
import SupportPopup from "@/components/SupportPopup.vue";
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { animationAPI, animationProcessingAPI } from "@/services/api";
import { CHARACTER_MODEL_URL } from "@/utils/characterModel";

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { BVHLoader } from "three/examples/jsm/loaders/BVHLoader";
import { AnimationRetargeting } from "@/composables/AnimationRetargeting";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

function setPlaybackSpeed(speed) {
  animationActions.forEach((action) => {
    action.timeScale = speed;
  });
}

const props = defineProps({
  animationId: {
    type: String,
    required: false,
    default: null,
  },
  sessionId: {
    type: String,
    required: false,
    default: null,
  },
  taskId: {
    type: String,
    required: false,
    default: null,
  },
});

const emit = defineEmits(["loadError", "loading", "loaded"]);

const threejsContainer = ref(null);
const clock = new THREE.Clock();
let scene, camera, renderer, controls, loadedScene, skeletonMesh;
const mixers = [];
let animationActions = [];
let activeClip = null;
let animationRequestId = 0;
let renderFrameId = null;
let isDisposed = false;
const transitionFrameIds = new Set();
const pendingDelays = new Set();
let initialSceneTransform = null;
let initialBoneTransforms = [];

const iconName = ref("pause");
const isPanModeEnabled = ref(false);
const zoomStep = 1;

const progressBarWidth = ref(0);
const animationDetails = reactive({
  duration: 0,
  currentTime: 0,
});
const isAnimationPlaying = ref(true);

const formattedCurrentTime = computed(() =>
  formatTime(animationDetails.currentTime)
);
const formattedDuration = computed(() => formatTime(animationDetails.duration));

const animationFPS = ref(30);

const currentFrame = ref(0);
const totalFrames = computed(() => {
  if (animationActions.length > 0 && animationActions[0].getClip()) {
    return animationActions[0].getClip().duration * animationFPS.value;
  }
  return 0;
});

const dragging = ref(false);


// Detected bone prefix for the current model (e.g. "mixamorig" or "mixamorig8")
const bonePrefix = ref("mixamorig");
const TASK_POLL_INTERVAL_MS = 3000;
const processingLabel = () => t('viewer.loading.processing');
const processingSubtitle = () => t('viewer.loading.processingSubtitle');

function delay(ms) {
  return new Promise((resolve) => {
    const delayEntry = {
      id: window.setTimeout(() => {
        pendingDelays.delete(delayEntry);
        resolve(!isDisposed);
      }, ms),
      resolve,
    };
    pendingDelays.add(delayEntry);
  });
}

function isViewerActive() {
  return !isDisposed && renderer && scene && camera && controls;
}

function scheduleTransition(callback) {
  const frameId = requestAnimationFrame(() => {
    transitionFrameIds.delete(frameId);
    callback();
  });
  transitionFrameIds.add(frameId);
}

function cancelPendingViewerWork() {
  animationRequestId += 1;

  if (renderFrameId !== null) {
    cancelAnimationFrame(renderFrameId);
    renderFrameId = null;
  }

  transitionFrameIds.forEach((frameId) => cancelAnimationFrame(frameId));
  transitionFrameIds.clear();

  pendingDelays.forEach((delayEntry) => {
    clearTimeout(delayEntry.id);
    delayEntry.resolve(false);
  });
  pendingDelays.clear();
}

async function waitForMocapTask() {
  if (!props.taskId || !props.sessionId) {
    return true;
  }

  let isCompleted = false;

  while (!isCompleted && !isDisposed) {
    const response = await animationProcessingAPI.checkMocapProgress(
      props.taskId,
      props.sessionId
    );
    if (isDisposed) return false;
    const taskStatus = response?.status || {};
    const statusValue =
      typeof taskStatus.status === "string" ? taskStatus.status.trim().toLowerCase() : "";
    const taskResult = taskStatus.result;
    const taskProgress = taskStatus.progress;

    if (taskProgress && Number(taskProgress.total) > 0) {
      const current = Number(taskProgress.current) || 0;
      const total = Number(taskProgress.total) || 0;
      const percent =
        typeof taskProgress.progress === "number"
          ? Math.max(0, Math.min(taskProgress.progress * 100, 100))
          : Math.max(0, Math.min((current / total) * 100, 100));

      emit("loading", {
        processing: true,
        progress: percent,
        loaded: current,
        total,
        label: processingLabel(),
        subtitle: processingSubtitle(),
      });
    } else {
      emit("loading", {
        processing: true,
        label: processingLabel(),
        subtitle: processingSubtitle(),
      });
    }

    if (["failed", "error", "cancelled", "canceled"].includes(statusValue)) {
      throw new Error(`Animation processing failed: ${statusValue}`);
    }

    if (
      ["completed", "complete", "done", "finished", "success", "ready"].includes(statusValue) ||
      (props.animationId && taskResult === props.animationId)
    ) {
      isCompleted = true;
      continue;
    }

    if (!(await delay(TASK_POLL_INTERVAL_MS))) return false;
  }

  return !isDisposed;
}

function nextFrame() {
  if (currentFrame.value < totalFrames.value - 1) {
    currentFrame.value++;
    updateAnimationFrame();
  }
}

function previousFrame() {
  if (currentFrame.value > 0) {
    currentFrame.value--;
    updateAnimationFrame();
  }
}

function resetToFirstFrame() {
  currentFrame.value = 0;
  updateAnimationFrame();
}

function updateAnimationFrame() {
  const timePerFrame = 1 / animationFPS.value;
  const newTime = currentFrame.value * timePerFrame;
  animationActions.forEach((action) => {
    action.paused = true;
    action.time = newTime;
    action.play();
    action.paused = true;
  });

  animationDetails.currentTime = newTime;
  progressBarWidth.value = (newTime / animationDetails.duration) * 100;
}

function handleKeyPress(event) {
  if (event.target.tagName === "INPUT") return;

  switch (event.code) {
    case "Space":
      event.preventDefault();
      toggleAnimation();
      break;
    case "ArrowRight":
      event.preventDefault();
      nextFrame();
      break;
    case "ArrowLeft":
      event.preventDefault();
      previousFrame();
      break;
    case "Home":
      event.preventDefault();
      resetToFirstFrame();
      break;
  }
}

const showSupportPopup = ref(false);

function openSupportPopup() {
  showSupportPopup.value = true;
}

onMounted(() => {
  isDisposed = false;
  if (threejsContainer.value) {
    initThreeJS();
    addGridFloor();
    animate();

    document.addEventListener("keydown", handleKeyPress);

    // Load static model by default
    loadStaticModel();
  }
});

onBeforeUnmount(() => {
  isDisposed = true;
  cancelPendingViewerWork();
  document.removeEventListener("keydown", handleKeyPress);
  window.removeEventListener("resize", onWindowResize);

  cleanupCurrentScene();
  if (controls) {
    controls.dispose();
    controls = null;
  }
  if (renderer) {
    renderer.dispose();
    renderer.forceContextLoss();
    renderer = null;
  }
  scene = null;
  camera = null;
});

// BVH bone suffixes mapped to Mixamo bone suffixes
// The prefix (e.g. "mixamorig" or "mixamorig8") is prepended at runtime
const bvhBoneSuffixes = {
  Hips: "Hips",
  Spine: "Spine",
  Spine1: "Spine1",
  Spine2: "Spine2",
  Neck: "Neck",
  Head: "Head",
  LeftShoulder: "LeftShoulder",
  RightShoulder: "RightShoulder",
  LeftArm: "LeftArm",
  RightArm: "RightArm",
  LeftForeArm: "LeftForeArm",
  RightForeArm: "RightForeArm",
  LeftHand: "LeftHand",
  RightHand: "RightHand",
  LeftUpLeg: "LeftUpLeg",
  RightUpLeg: "RightUpLeg",
  LeftLeg: "LeftLeg",
  RightLeg: "RightLeg",
  LeftFoot: "LeftFoot",
  RightFoot: "RightFoot",
  // Toes (BVH uses LeftToe/RightToe, Mixamo uses LeftToeBase/RightToeBase)
  LeftToe: "LeftToeBase",
  RightToe: "RightToeBase",
  // Left hand fingers
  LeftHandThumb1: "LeftHandThumb1",
  LeftHandThumb2: "LeftHandThumb2",
  LeftHandThumb3: "LeftHandThumb3",
  LeftHandIndex1: "LeftHandIndex1",
  LeftHandIndex2: "LeftHandIndex2",
  LeftHandIndex3: "LeftHandIndex3",
  LeftHandMiddle1: "LeftHandMiddle1",
  LeftHandMiddle2: "LeftHandMiddle2",
  LeftHandMiddle3: "LeftHandMiddle3",
  LeftHandRing1: "LeftHandRing1",
  LeftHandRing2: "LeftHandRing2",
  LeftHandRing3: "LeftHandRing3",
  LeftHandPinky1: "LeftHandPinky1",
  LeftHandPinky2: "LeftHandPinky2",
  LeftHandPinky3: "LeftHandPinky3",
  // Right hand fingers
  RightHandThumb1: "RightHandThumb1",
  RightHandThumb2: "RightHandThumb2",
  RightHandThumb3: "RightHandThumb3",
  RightHandIndex1: "RightHandIndex1",
  RightHandIndex2: "RightHandIndex2",
  RightHandIndex3: "RightHandIndex3",
  RightHandMiddle1: "RightHandMiddle1",
  RightHandMiddle2: "RightHandMiddle2",
  RightHandMiddle3: "RightHandMiddle3",
  RightHandRing1: "RightHandRing1",
  RightHandRing2: "RightHandRing2",
  RightHandRing3: "RightHandRing3",
  RightHandPinky1: "RightHandPinky1",
  RightHandPinky2: "RightHandPinky2",
  RightHandPinky3: "RightHandPinky3",
};

function buildBoneMapping(prefix) {
  const mapping = {};
  for (const [bvhName, suffix] of Object.entries(bvhBoneSuffixes)) {
    mapping[bvhName] = prefix + suffix;
  }
  return mapping;
}

function loadBVHAnimation(bvhContent) {
  try {
    resetAnimationState();

    const bvhLoader = new BVHLoader();
    const bvh = bvhLoader.parse(bvhContent);

    if (!bvh || !bvh.skeleton || !bvh.clip) {
      console.error("[BVH] Invalid BVH data");
      emit("loadError", t('viewer.errors.parseBvh'));
      return;
    }

    // Detect FPS from BVH track time deltas
    if (bvh.clip.tracks.length > 0 && bvh.clip.tracks[0].times.length >= 2) {
      const frameDelta = bvh.clip.tracks[0].times[1] - bvh.clip.tracks[0].times[0];
      if (frameDelta > 0) {
        animationFPS.value = Math.round(1 / frameDelta);
      }
    }

    // Log BVH source skeleton
    const bvhBoneNames = [];
    bvh.skeleton.bones.forEach((bone) => bvhBoneNames.push(bone.name));
    console.log("[BVH] Source skeleton bones:", bvhBoneNames.length, bvhBoneNames);

    // Log target model skeleton
    const targetBoneNames = [];
    loadedScene.traverse((obj) => {
      if (obj.isBone) targetBoneNames.push(obj.name);
    });
    console.log("[BVH] Target model bones:", targetBoneNames.length, targetBoneNames);

    // Log mapping being used
    const mapping = buildBoneMapping(bonePrefix.value);
    console.log("[BVH] Using bone prefix:", bonePrefix.value);
    console.log("[BVH] Bone mapping:", mapping);

    // Check which mapped target bones actually exist in the model
    const targetBoneSet = new Set(targetBoneNames);
    const matched = [];
    const unmatched = [];
    for (const [bvhName, targetName] of Object.entries(mapping)) {
      if (targetBoneSet.has(targetName)) {
        matched.push(bvhName + " -> " + targetName);
      } else {
        unmatched.push(bvhName + " -> " + targetName + " (MISSING)");
      }
    }
    console.log("[BVH] Matched bones:", matched.length, matched);
    if (unmatched.length > 0) {
      console.warn("[BVH] Unmatched bones:", unmatched.length, unmatched);
    }

    // Save raw BVH root position values before retargeting (world-space, meters)
    const rootBoneName = bvh.skeleton.bones[0].name;
    const bvhHipsTrack = bvh.clip.tracks.find(
      t => t.name === rootBoneName + '.position'
    );
    let rawBvhPositions = null;
    if (bvhHipsTrack) {
      rawBvhPositions = new Float32Array(bvhHipsTrack.values);
    }

    // Retarget BVH animation to Mixamo skeleton (handles rotation tracks correctly)
    const retargeter = new AnimationRetargeting(bvh.skeleton, loadedScene, {
      boneNameMap: mapping,
      trgPoseMode: AnimationRetargeting.BindPoseModes.CURRENT,
      trgEmbedWorldTransforms: true,
    });
    const retargetedClip = retargeter.retargetAnimation(bvh.clip);

    // Fix root position track: the retargeter misses the Armature's scale factor.
    // BVH positions are world-space meters; the target bone expects local-space
    // values in the Armature's coordinate system (cm, rotated).
    // Convert by transforming BVH world positions through the Armature's inverse
    // world matrix — this matches how GLB bakes its Hips.position track.
    if (rawBvhPositions) {
      const trgHipsName = mapping['Hips'] || mapping[rootBoneName];
      let trgHipsBone = null;
      loadedScene.traverse((obj) => {
        if (obj.isBone && obj.name === trgHipsName) trgHipsBone = obj;
      });

      if (trgHipsBone && trgHipsBone.parent) {
        // Get Armature (parent) inverse world matrix — converts world → local
        trgHipsBone.parent.updateWorldMatrix(true, false);
        const armatureInvMatrix = new THREE.Matrix4().copy(trgHipsBone.parent.matrixWorld).invert();

        const posTrackIdx = retargetedClip.tracks.findIndex(
          t => t.name === trgHipsName + '.position'
        );

        const newValues = new Float32Array(rawBvhPositions.length);
        const pos = new THREE.Vector3();
        for (let i = 0; i < rawBvhPositions.length; i += 3) {
          pos.set(rawBvhPositions[i], rawBvhPositions[i + 1], rawBvhPositions[i + 2]);
          pos.applyMatrix4(armatureInvMatrix);
          newValues[i] = pos.x;
          newValues[i + 1] = pos.y;
          newValues[i + 2] = pos.z;
        }

        // Replace or insert the corrected position track
        const correctedTrack = new THREE.VectorKeyframeTrack(
          trgHipsName + '.position',
          bvhHipsTrack.times.slice(),
          newValues
        );

        if (posTrackIdx >= 0) {
          retargetedClip.tracks[posTrackIdx] = correctedTrack;
        } else {
          retargetedClip.tracks.push(correctedTrack);
        }

      }
    }

    activeClip = retargetedClip;

    // Create a mixer per SkinnedMesh so all mesh parts animate correctly.
    // FBX models can have multiple SkinnedMesh objects with separate skeleton
    // instances; a single mixer on loadedScene would only animate the first
    // skeleton found by PropertyBinding, leaving other mesh parts frozen.
    const skinnedMeshes = [];
    loadedScene.traverse((obj) => {
      if (obj.isSkinnedMesh) skinnedMeshes.push(obj);
    });

    mixers.length = 0;
    animationActions.length = 0;

    const mixerRoots = skinnedMeshes.length > 0 ? skinnedMeshes : [loadedScene];
    mixerRoots.forEach((root) => {
      const mixer = new THREE.AnimationMixer(root);
      mixers.push(mixer);
      const action = mixer.clipAction(retargetedClip);
      animationActions.push(action);
      action.play();
    });

    animationDetails.duration = retargetedClip.duration;
    animationDetails.currentTime = 0;
    progressBarWidth.value = 0;
    isAnimationPlaying.value = true;
    iconName.value = "pause";

    // Offset model so feet sit on the ground plane (Y=0).
    // Evaluate frame 0, find the lowest foot bone, and shift up.
    mixers.forEach((m) => m.update(0));
    loadedScene.updateMatrixWorld(true);
    let lowestY = 0;
    const worldPos = new THREE.Vector3();
    loadedScene.traverse((obj) => {
      if (obj.isBone) {
        obj.getWorldPosition(worldPos);
        if (worldPos.y < lowestY) lowestY = worldPos.y;
      }
    });
    if (lowestY < 0) {
      loadedScene.position.y -= lowestY;
    }
  } catch (error) {
    console.error("[BVH] Error loading BVH animation:", error);
    emit("loadError", t('viewer.errors.loadBvh', { error: error.message || t('common.unknownError') }));
  }
}

async function downloadAndApplyBVHAnimation(inPlace = false) {
  if (isDisposed) return false;
  if (!props.animationId || !props.sessionId) {
    emit("loaded");
    return true;
  }

  const requestId = ++animationRequestId;
  emit("loading", { progress: 0, label: t('viewer.loading.animation'), subtitle: t('viewer.loading.downloadingAnimation') });

  try {
    const taskCompleted = await waitForMocapTask();
    if (!taskCompleted || isDisposed || requestId !== animationRequestId) return false;

    let response = await animationAPI.downloadBVHAnimation(
      props.animationId,
      props.sessionId,
      inPlace
    );

    // TODO:
    // Temporary fallback for the backend transition period:
    // if the new progress-status flow has already reported completion but BVH files
    // are still not available, the legacy endpoint can still respond with 206.
    // This branch should likely be removed once the backend contract is fully stable.
    // 206 means the animation is still being processed on the server
    if (response.status === 206) {
      emit("loading", {
        processing: true,
        progress: 0,
        label: processingLabel(),
        subtitle: processingSubtitle(),
      });

      // Poll until the animation is ready
      while (response.status === 206 && !isDisposed) {
        if (!(await delay(TASK_POLL_INTERVAL_MS))) return false;
        response = await animationAPI.downloadBVHAnimation(
          props.animationId,
          props.sessionId,
          inPlace
        );
      }

      if (isDisposed || requestId !== animationRequestId) return false;
      emit("loading", { progress: 0, label: t('viewer.loading.animation'), subtitle: t('viewer.loading.applyingAnimation') });
    }

    if (!response.ok) throw new Error("Failed to download animation");
    if (isDisposed || requestId !== animationRequestId) return false;

    const bvhContent = await response.text();
    if (isDisposed || requestId !== animationRequestId) return false;
    loadBVHAnimation(bvhContent);
    emit("loaded");
    return true;
  } catch (error) {
    if (isDisposed || requestId !== animationRequestId) return false;
    console.error("[BVH] Error downloading BVH animation:", error);
    emit("loadError", t('viewer.errors.downloadBvh', { error: error.message || t('common.unknownError') }));
    return false;
  }
}

function resetAnimationState() {
  if (!loadedScene) return;

  mixers.forEach((mixer) => mixer.stopAllAction());
  mixers.length = 0;
  animationActions.length = 0;
  activeClip = null;
  iconName.value = "pause";
  isAnimationPlaying.value = true;
  currentFrame.value = 0;
  animationDetails.duration = 0;
  animationDetails.currentTime = 0;
  progressBarWidth.value = 0;

  if (initialSceneTransform) {
    loadedScene.position.copy(initialSceneTransform.position);
    loadedScene.quaternion.copy(initialSceneTransform.quaternion);
    loadedScene.scale.copy(initialSceneTransform.scale);
  }

  initialBoneTransforms.forEach(({ bone, position, quaternion, scale }) => {
    bone.position.copy(position);
    bone.quaternion.copy(quaternion);
    bone.scale.copy(scale);
  });

  loadedScene.updateMatrixWorld(true);
}

async function reloadBVHAnimation(options = {}) {
  const { inPlace = false } = options;
  return downloadAndApplyBVHAnimation(inPlace);
}



function cleanupCurrentScene() {
  // Clean up loaded scene geometry/materials
  if (loadedScene) {
    scene?.remove(loadedScene);
    loadedScene.traverse((object) => {
      if (object.isMesh) {
        object.geometry?.dispose();
        if (object.material) {
          const disposeMaterial = (material) => {
            Object.values(material).forEach((value) => {
              if (value?.isTexture) value.dispose();
            });
            material.dispose();
          };
          if (Array.isArray(object.material)) {
            object.material.forEach(disposeMaterial);
          } else {
            disposeMaterial(object.material);
          }
        }
      }
    });
    loadedScene = null;
  }

  activeClip = null;

  // Clean up animations
  mixers.forEach((mixer) => mixer.stopAllAction());
  mixers.length = 0;
  animationActions.length = 0;
  animationDetails.duration = 0;
  animationDetails.currentTime = 0;
  progressBarWidth.value = 0;

  // Clean up skeleton
  if (skeletonMesh) {
    skeletonMesh.forEach(({ mesh }) => {
      scene?.remove(mesh);
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    });
    skeletonMesh = null;
  }
}

function loadStaticModel() {
  if (isDisposed) return;
  cleanupCurrentScene();
  emit("loading", {
    progress: 0,
    label: t('viewer.loading.model'),
    subtitle: t('viewer.loading.modelSubtitle'),
  });

  try {
    const loader = new GLTFLoader();

    loader.load(
      CHARACTER_MODEL_URL,
      (gltf) => {
        if (isDisposed) return;
        handleGLTFLoaded(gltf);
        downloadAndApplyBVHAnimation();
      },
      (progress) => {
        if (isDisposed) return;
        if (progress.total > 0) {
          const pct = Math.round((progress.loaded / progress.total) * 100);
          emit("loading", {
            progress: pct,
            loaded: progress.loaded,
            total: progress.total,
            label: t('viewer.loading.model'),
            subtitle: t('viewer.loading.modelSubtitle'),
          });
        }
      },
      (error) => {
        if (isDisposed) return;
        console.error("[Model] Error loading static model:", error);
        emit("loadError", t('viewer.errors.loadModel', { error: t('common.unknownError') }));
      }
    );
  } catch (error) {
    if (isDisposed) return;
    console.error("[Model] Error in loadStaticModel:", error);
    emit("loadError", t('viewer.errors.loadModel', { error: error.message || t('common.unknownError') }));
  }
}

function initThreeJS() {
  const width = threejsContainer.value.clientWidth;
  const height = threejsContainer.value.clientHeight;

  scene = new THREE.Scene();
  scene.background = new THREE.Color("rgb(55, 55, 55)");

  camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
  camera.position.set(0, 5, 10);

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(width, height);
  threejsContainer.value.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.enablePan = true;

  setupLights();

  window.addEventListener("resize", onWindowResize);
}

function onWindowResize() {
  if (camera && renderer && threejsContainer.value) {
    const width = threejsContainer.value.clientWidth;
    const height = threejsContainer.value.clientHeight;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  }
}

function setupLights() {
  const ambientLight = new THREE.AmbientLight(0xffffff, 1);
  const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
  directionalLight1.position.set(1, 1, 0).normalize();

  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 1.2);
  directionalLight2.position.set(-1, 1, 0).normalize();

  const directionalLight3 = new THREE.DirectionalLight(0xffffff, 1.2);
  directionalLight3.position.set(0, 1, 1).normalize();

  scene.add(
    ambientLight,
    directionalLight1,
    directionalLight2,
    directionalLight3
  );
}

function handleGLTFLoaded(gltf) {
  if (!isViewerActive()) return;
  loadedScene = gltf.scene;

  scene.add(loadedScene);
  captureInitialModelState();

  skeletonMesh = createBlenderStyleSkeleton(loadedScene);
  setupGLTFModel(gltf);
}

function captureInitialModelState() {
  if (!loadedScene) return;

  initialSceneTransform = {
    position: loadedScene.position.clone(),
    quaternion: loadedScene.quaternion.clone(),
    scale: loadedScene.scale.clone(),
  };

  initialBoneTransforms = [];
  loadedScene.traverse((obj) => {
    if (!obj.isBone) return;
    initialBoneTransforms.push({
      bone: obj,
      position: obj.position.clone(),
      quaternion: obj.quaternion.clone(),
      scale: obj.scale.clone(),
    });
  });
}

function setupGLTFModel(gltf) {
  // Only setup GLTF animations if we don't have a BVH animation to apply
  if (!props.animationId && gltf.animations && gltf.animations.length) {
    setupGLTFAnimations(gltf.animations);
  }
  adjustCameraToFitModel();
}

function setupGLTFAnimations(animations) {
  const mixer = new THREE.AnimationMixer(loadedScene);
  mixers.push(mixer);
  animations.forEach((clip) => {
    const action = mixer.clipAction(clip);
    action.play();
    animationActions.push(action);
  });

  activeClip = animations[0] || null;

  animationDetails.duration = mixer
    .clipAction(animations[0])
    .getClip().duration;
}

function createBlenderStyleSkeleton(skeleton) {
  const boneMeshes = [];

  // Define the main spine chain in order using detected prefix
  const p = bonePrefix.value;
  const mainSpineChain = [
    p + "Hips",
    p + "Spine",
    p + "Spine2",
    p + "Neck",
    p + "Head",
  ];

  // Map bone names to bone objects
  const boneMap = {};
  skeleton.traverse((bone) => {
    if (bone.isBone) {
      boneMap[bone.name] = bone;
    }
  });

  // Draw pelvis bone
  const leftHip = boneMap[p + "LeftUpLeg"];
  const rightHip = boneMap[p + "RightUpLeg"];
  const spine = boneMap[p + "Spine"];

  if (leftHip && rightHip && spine) {
    // Calculate pelvis center and orientation
    const leftPos = new THREE.Vector3();
    const rightPos = new THREE.Vector3();
    const spinePos = new THREE.Vector3();

    leftPos.setFromMatrixPosition(leftHip.matrixWorld);
    rightPos.setFromMatrixPosition(rightHip.matrixWorld);
    spinePos.setFromMatrixPosition(spine.matrixWorld);

    // Calculate pelvis center (slightly above the hip line)
    const hipMidpoint = leftPos.clone().add(rightPos).multiplyScalar(0.5);
    const pelvisCenter = hipMidpoint.clone().add(spinePos).multiplyScalar(0.5);

    // Calculate pelvis width and height
    const hipWidth = leftPos.distanceTo(rightPos);
    const pelvisHeight = spinePos.distanceTo(hipMidpoint) * 0.8; // Make pelvis shorter than full height

    // Create pelvis bone geometry
    const pelvisGeometry = new THREE.CylinderGeometry(
      hipWidth * 0.05, // Top radius
      hipWidth * 0.12, // Bottom radius
      pelvisHeight, // Height
      4 // Segments
    );

    const material = new THREE.MeshBasicMaterial({ color: "orange" });
    const edgeMaterial = new THREE.LineBasicMaterial({ color: "black" });

    const pelvisBone = new THREE.Mesh(pelvisGeometry, material);
    const edges = new THREE.EdgesGeometry(pelvisGeometry);
    const edgeLines = new THREE.LineSegments(edges, edgeMaterial);
    pelvisBone.add(edgeLines);

    // Position pelvis bone
    pelvisBone.position.copy(pelvisCenter);

    // Calculate rotation to align with hips
    const hipDirection = new THREE.Vector3()
      .subVectors(rightPos, leftPos)
      .normalize(); // X axis
    const upDirection = new THREE.Vector3()
      .subVectors(spinePos, hipMidpoint)
      .normalize(); // Y axis
    const zDirection = new THREE.Vector3()
      .crossVectors(hipDirection, upDirection)
      .normalize(); // Z axis
    // Re-orthogonalize upDirection in case of non-orthogonality
    upDirection.crossVectors(zDirection, hipDirection).normalize();
    // Build rotation matrix
    const matrix = new THREE.Matrix4();
    matrix.makeBasis(hipDirection, upDirection, zDirection);
    pelvisBone.setRotationFromMatrix(matrix);

    pelvisBone.visible = false;
    scene.add(pelvisBone);

    boneMeshes.push({
      mesh: pelvisBone,
      parent: leftHip,
      child: rightHip,
      length: hipWidth,
      isPelvis: true,
    });
  }

  // Draw the spine as a chain of segments (orange)
  for (let i = 0; i < mainSpineChain.length - 1; i++) {
    const boneA = boneMap[mainSpineChain[i]];
    const boneB = boneMap[mainSpineChain[i + 1]];
    if (boneA && boneB) {
      const start = new THREE.Vector3();
      const end = new THREE.Vector3();
      start.setFromMatrixPosition(boneA.matrixWorld);
      end.setFromMatrixPosition(boneB.matrixWorld);

      const length = end.distanceTo(start);
      const baseRadius = length * 0.1;
      const height = length;

      const geometry = new THREE.CylinderGeometry(0, baseRadius, height, 4);
      const material = new THREE.MeshBasicMaterial({ color: "orange" });
      const edgeMaterial = new THREE.LineBasicMaterial({ color: "black" });

      const pyramid = new THREE.Mesh(geometry, material);
      const edges = new THREE.EdgesGeometry(geometry);
      const edgeLines = new THREE.LineSegments(edges, edgeMaterial);
      pyramid.add(edgeLines);

      // Position at midpoint
      const midpoint = start.clone().add(end).multiplyScalar(0.5);
      pyramid.position.copy(midpoint);

      // Set rotation from boneA to boneB
      const direction = new THREE.Vector3().subVectors(end, start).normalize();
      const up = new THREE.Vector3(0, 1, 0);
      const quaternion = new THREE.Quaternion().setFromUnitVectors(
        up,
        direction
      );
      pyramid.setRotationFromQuaternion(quaternion);

      pyramid.scale.set(1, 1, 1);
      pyramid.visible = false;
      scene.add(pyramid);

      boneMeshes.push({ mesh: pyramid, parent: boneA, child: boneB, length });
    }
  }

  // Draw all other bones except the main spine chain
  skeleton.traverse((bone) => {
    if (bone.isBone && !mainSpineChain.includes(bone.name)) {
      bone.children.forEach((child) => {
        if (child.isBone && !mainSpineChain.includes(child.name)) {
          // Draw as before (orange)
          const start = new THREE.Vector3();
          const end = new THREE.Vector3();
          start.setFromMatrixPosition(bone.matrixWorld);
          end.setFromMatrixPosition(child.matrixWorld);

          const length = end.distanceTo(start);
          const baseRadius = length * 0.1;
          const height = length;

          const geometry = new THREE.CylinderGeometry(0, baseRadius, height, 4);
          const material = new THREE.MeshBasicMaterial({ color: "orange" });
          const edgeMaterial = new THREE.LineBasicMaterial({ color: "black" });

          const pyramid = new THREE.Mesh(geometry, material);
          const edges = new THREE.EdgesGeometry(geometry);
          const edgeLines = new THREE.LineSegments(edges, edgeMaterial);
          pyramid.add(edgeLines);

          const midpoint = start.clone().add(end).multiplyScalar(0.5);
          pyramid.position.copy(midpoint);

          // Use the parent bone's world matrix for rotation
          const worldMatrix = bone.matrixWorld.clone();
          const position = new THREE.Vector3();
          const quaternion = new THREE.Quaternion();
          const scale = new THREE.Vector3();
          worldMatrix.decompose(position, quaternion, scale);

          pyramid.setRotationFromQuaternion(quaternion);
          pyramid.scale.set(1, 1, 1);

          pyramid.visible = false;
          scene.add(pyramid);

          boneMeshes.push({ mesh: pyramid, parent: bone, child, length });
        }
      });
    }
  });

  return boneMeshes;
}

function updateConeSkeleton(boneMeshes) {
  boneMeshes.forEach(({ mesh, parent, child, length, isPelvis }) => {
    if (isPelvis) {
      // Update pelvis position and rotation
      const leftPos = new THREE.Vector3();
      const rightPos = new THREE.Vector3();
      const spinePos = new THREE.Vector3();

      leftPos.setFromMatrixPosition(parent.matrixWorld);
      rightPos.setFromMatrixPosition(child.matrixWorld);
      spinePos.setFromMatrixPosition(parent.parent.matrixWorld);

      // Calculate new pelvis center
      const hipMidpoint = leftPos.clone().add(rightPos).multiplyScalar(0.5);
      const pelvisCenter = hipMidpoint
        .clone()
        .add(spinePos)
        .multiplyScalar(0.5);

      // Update position
      mesh.position.copy(pelvisCenter);

      // Update rotation
      const hipDirection = new THREE.Vector3()
        .subVectors(rightPos, leftPos)
        .normalize();
      const upDirection = new THREE.Vector3()
        .subVectors(spinePos, hipMidpoint)
        .normalize();
      const zDirection = new THREE.Vector3()
        .crossVectors(hipDirection, upDirection)
        .normalize();
      upDirection.crossVectors(zDirection, hipDirection).normalize();
      const matrix = new THREE.Matrix4();
      matrix.makeBasis(hipDirection, upDirection, zDirection);
      mesh.setRotationFromMatrix(matrix);

      return;
    }

    // Get updated positions in world space
    const start = new THREE.Vector3();
    const end = new THREE.Vector3();
    start.setFromMatrixPosition(parent.matrixWorld);
    end.setFromMatrixPosition(child.matrixWorld);

    const midpoint = start.clone().add(end).multiplyScalar(0.5);
    mesh.position.copy(midpoint);

    // Use the parent bone's world matrix for rotation
    const worldMatrix = parent.matrixWorld.clone();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    worldMatrix.decompose(position, quaternion, scale);

    // Apply the bone's rotation
    mesh.setRotationFromQuaternion(quaternion);

    // Update scale to maintain bone length
    const currentLength = end.distanceTo(start);
    const scaleFactor = currentLength / length;
    mesh.scale.set(1, scaleFactor, 1);
  });
}

function adjustCameraToFitModel() {
  const box = new THREE.Box3().setFromObject(loadedScene);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const distance = size.length() * 1.5;

  const startPosition = camera.position.clone();
  const endPosition = new THREE.Vector3(
    center.x,
    center.y,
    center.z + distance
  );

  const duration = 1.0;
  const startTime = clock.getElapsedTime();

  function animate() {
    if (!isViewerActive()) return;
    const elapsedTime = clock.getElapsedTime() - startTime;
    const progress = Math.min(elapsedTime / duration, 1);

    // Smooth easing
    const easeProgress = 1 - Math.pow(1 - progress, 3);

    camera.position.lerpVectors(startPosition, endPosition, easeProgress);
    controls.target.lerp(center, easeProgress);

    if (progress < 1) {
      scheduleTransition(animate);
    }
  }

  animate();
}

function formatTime(seconds) {
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);
  return `${secs.toString().padStart(2, "0")}:${ms
    .toString()
    .padStart(2, "0")}`;
}

function animate() {
  if (!isViewerActive()) return;
  renderFrameId = requestAnimationFrame(animate);
  const delta = clock.getDelta();

  // Resize renderer if container size changed (e.g. after v-show toggle)
  if (threejsContainer.value && renderer) {
    const w = threejsContainer.value.clientWidth;
    const h = threejsContainer.value.clientHeight;
    if (w > 0 && h > 0 && (renderer.domElement.width !== w || renderer.domElement.height !== h)) {
      onWindowResize();
    }
  }

  if (isAnimationPlaying.value) {
    mixers.forEach((mixer) => mixer.update(delta));
  } else {
    mixers.forEach((mixer) => mixer.update(0));
  }

  if (skeletonMesh) {
    updateConeSkeleton(skeletonMesh);
  }

  if (animationActions.length > 0) {
    const currentMixerTime =
      animationActions[0].time % animationDetails.duration;
    animationDetails.currentTime = currentMixerTime;
    progressBarWidth.value =
      (currentMixerTime / animationDetails.duration) * 100;
  }

  controls.update();
  renderer.render(scene, camera);
}

function zoomIn() {
  const direction = new THREE.Vector3()
    .subVectors(camera.position, controls.target)
    .normalize();
  const targetPosition = camera.position
    .clone()
    .addScaledVector(direction, -zoomStep);
  initiateZoomTransition(targetPosition);
}

function zoomOut() {
  const direction = new THREE.Vector3()
    .subVectors(camera.position, controls.target)
    .normalize();
  const targetPosition = camera.position
    .clone()
    .addScaledVector(direction, zoomStep);
  initiateZoomTransition(targetPosition);
}

function initiateZoomTransition(targetPosition) {
  const startPosition = camera.position.clone();
  const duration = 0.5;
  const startTime = clock.getElapsedTime();

  function zoomTransition() {
    if (!isViewerActive()) return;
    const elapsedTime = clock.getElapsedTime() - startTime;
    const progress = Math.min(elapsedTime / duration, 1);

    camera.position.lerpVectors(startPosition, targetPosition, progress);
    controls.update();

    if (progress < 1) {
      scheduleTransition(zoomTransition);
    }
  }

  zoomTransition();
}

function togglePanMode() {
  isPanModeEnabled.value = !isPanModeEnabled.value;
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN,
  };

  if (isPanModeEnabled.value) {
    controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
    controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
  }

  controls.update();
}

function toggleSkeletonVisibility() {
  if (skeletonMesh) {
    skeletonMesh.forEach(({ mesh }) => {
      mesh.visible = !mesh.visible;
    });
  }

  if (loadedScene) {
    loadedScene.visible = !loadedScene.visible;
  }

  controls.update();
}

function toggleAnimation() {
  isAnimationPlaying.value = !isAnimationPlaying.value;
  iconName.value = isAnimationPlaying.value ? "pause" : "play";

  animationActions.forEach((action) => {
    action.paused = !isAnimationPlaying.value;
    if (!isAnimationPlaying.value) {
      action.play();
    }
  });

  if (!isAnimationPlaying.value) {
    currentFrame.value = Math.round(animationDetails.currentTime * animationFPS.value);
  }
}

function addGridFloor() {
  const gridHelper = new THREE.GridHelper(500, 500, 0x555555, 0x444444);
  gridHelper.position.y = 0;
  gridHelper.material.opacity = 0.75;
  gridHelper.material.transparent = true;
  scene.add(gridHelper);
}

function resetCamera() {
  adjustCameraToFitModel();
}

function setAnimationProgress(event) {
  const bounds = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - bounds.left;
  const progressPercentage = clickX / bounds.width;
  const newTime = progressPercentage * animationDetails.duration;

  animationActions.forEach((action) => {
    action.paused = true;
    action.time = newTime;
    action.play();
    action.paused = !isAnimationPlaying.value;
  });

  animationDetails.currentTime = newTime;
  progressBarWidth.value = progressPercentage * 100;
}

function startDragging(event) {
  dragging.value = true;
  updateAnimationProgress(event);
}

function dragUpdate(event) {
  if (dragging.value) {
    updateAnimationProgress(event);
  }
}

function stopDragging() {
  dragging.value = false;
}

function updateAnimationProgress(event) {
  const bounds = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - bounds.left;
  const progressPercentage = clickX / bounds.width;
  const newTime = progressPercentage * animationDetails.duration;

  animationActions.forEach((action) => {
    action.paused = true;
    action.time = newTime;
    action.play();
    action.paused = !isAnimationPlaying.value;
  });

  animationDetails.currentTime = newTime;
  progressBarWidth.value = progressPercentage * 100;
}

async function exportGLB(options = {}) {
  const { GLTFExporter } = await import("three/examples/jsm/exporters/GLTFExporter");
  const exporter = new GLTFExporter();
  const animations = activeClip ? [activeClip] : [];
  const { includeSkin = true } = options;

  const exportRoot = includeSkin ? loadedScene : cloneAnimationSceneWithoutMeshes();
  if (!exportRoot) {
    throw new Error("Model is not loaded");
  }

  let skinnedMeshes = [];
  let savedSkeletons = [];

  if (includeSkin) {
    // Make all SkinnedMesh objects share one Skeleton so the exporter
    // produces a single Armature instead of one per mesh part
    loadedScene.traverse((obj) => {
      if (obj.isSkinnedMesh) skinnedMeshes.push(obj);
    });
    savedSkeletons = skinnedMeshes.map((m) => m.skeleton);
    if (skinnedMeshes.length > 1) {
      const shared = skinnedMeshes[0].skeleton;
      for (let i = 1; i < skinnedMeshes.length; i++) {
        skinnedMeshes[i].skeleton = shared;
      }
    }
  }

  // Export the full loadedScene (wrapper Group + Armature with 0.01 scale).
  // This preserves the original scale hierarchy so Blender displays bones correctly.
  const arrayBuffer = await exporter.parseAsync(exportRoot, { binary: true, animations });

  // Restore original skeleton references
  skinnedMeshes.forEach((m, i) => { m.skeleton = savedSkeletons[i]; });

  return arrayBuffer;
}

function cloneAnimationSceneWithoutMeshes() {
  if (!loadedScene) return null;

  const sceneClone = loadedScene.clone(true);
  const removableNodes = [];

  sceneClone.traverse((obj) => {
    if (obj.isMesh || obj.isSkinnedMesh) {
      removableNodes.push(obj);
    }
  });

  removableNodes.forEach((node) => {
    if (node.parent) {
      node.parent.remove(node);
    }
  });

  return sceneClone;
}

defineExpose({
  setPlaybackSpeed,
  resetCamera,
  toggleSkeletonVisibility,
  exportGLB,
  reloadBVHAnimation,
});
</script>

<style scoped>
.anim-controls,
.scale-controls {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
}

.control-btn {
  padding: var(--spacing-sm-lg);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color var(--transition-fast);
  border-radius: var(--border-radius-xs);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.control-btn:hover {
  background-color: var(--color-white-10);
}

.control-btn:active {
  background-color: var(--color-white-20);
}

.control-btn.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.progress-bar {
  background-color: var(--color-white);
  height: 0.7rem;
  width: 0%;
  min-width: 4px;
}

.animation-wrapper {
  position: relative;
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  height: calc(100vh - 200px);
  min-height: 500px;
}

.threejs-container {
  flex: 1;
  width: 100%;
  height: 100%;
  margin: 0 var(--spacing-md);
}

@media (max-height: 750px) {
  .animation-wrapper {
    height: calc(100vh - 180px);
    min-height: 400px;
  }
}

@media (max-width: 980px) {
  .animation-wrapper {
    height: calc(100vh - 220px);
  }

  .threejs-container {
    margin: 0 var(--spacing-sm);
  }
}

.animation-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) 0;
  gap: var(--spacing-md);
}

.playbar {
  flex: 1;
  max-width: 800px;
  min-width: 300px;
}

.progress-container {
  flex-grow: 1;
  height: 0.7rem;
  background-color: var(--color-white-15);
  border-radius: var(--border-radius-xs);
  margin: 0 var(--spacing-sm-lg);
  position: relative;
  cursor: pointer;
  margin-bottom: var(--spacing-xs-sm);
  user-select: none;
}

.time-codes {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.playbar-controls {
  display: flex;
  align-items: center;
  justify-content: center;
}

.playbar-controls .control-btn:not(:first-child) {
  margin-left: var(--spacing-sm-md);
}

.locked {
  cursor: not-allowed;
}

.rotate {
  transform: rotate(180deg);
}

.spacer {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .animation-wrapper {
    height: calc(100vh - 250px);
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .anim-controls,
  .scale-controls {
    flex-direction: row;
    gap: var(--spacing-sm);
    padding: 0;
  }

  .threejs-container {
    margin: 0;
  }

  .animation-footer {
    flex-wrap: wrap;
    gap: var(--spacing-sm);
  }

  .playbar {
    min-width: 250px;
  }

  .spacer {
    display: none;
  }
}
</style>
